import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import { Search, AttachMoney, ExpandMore, ArrowBack, Refresh } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { product } from "../../api/paquetes";
import { getUsers } from "../../api/users";
import { useNotification } from "../../context/notification.context";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

interface VendedorSaldo {
  id: string;
  usuarioId: number;
  vendedor: string;
  ganadoARS: number;
  ganadoUSD: number;
  cobrado: number;
  saldo: number;
}

const TAG_PAGO_VENDEDOR = "[PAGO_VENDEDOR]";

const normalizeVendorName = (value: any) => String(value || "").trim().toLowerCase();

const CuentaCorriente: React.FC = () => {
  const [terminoBusqueda, setTerminoBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [vendedoresData, setVendedoresData] = useState<VendedorSaldo[]>([]);
  const [paquetesRaw, setPaquetesRaw] = useState<any[]>([]);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState<VendedorSaldo | null>(null);
  const [cargando, setCargando] = useState(true);
  const { getError, getSuccess } = useNotification();

  // --- RBAC: determinar si el usuario es admin ---
  const userData = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);
  const isAdmin = userData.rol === "Superadministrador" || userData.rol === "Administrador";

  const [importesAPagar, setImportesAPagar] = useState<Record<string, string>>({});
  const [enviandoPago, setEnviandoPago] = useState(false);
  const [recalculando, setRecalculando] = useState<Set<number>>(new Set());
  const [recalculandoTodo, setRecalculandoTodo] = useState(false);
  const [tipoCambioHistory, setTipoCambioHistory] = useState<{ cotizacion: number; created_at: string }[]>([]);

  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      let response;
      let usuarios: any[] = [];

      if (isAdmin) {
        // Admins can fetch the full user list
        [response, usuarios] = await Promise.all([product.getAll(), getUsers()]);
      } else {
        // Non-admins don't have permission for getUsers(), so we just use their own session data
        response = await product.getAll();
        usuarios = [userData];
      }

      const payload = response.data;
      const paquetesAPI = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);

      const individuales = paquetesAPI.filter((p: any) => {
        const hasGrupalTag = p.descripcion && typeof p.descripcion === 'string' && p.descripcion.includes('[GRUPAL:');
        const hasGrupalObs = p.observaciones && typeof p.observaciones === 'string' && p.observaciones.includes('METADATA_GRUPAL');
        return !p.is_grupal && !hasGrupalTag && !hasGrupalObs;
      });

      setPaquetesRaw(individuales);

      // Cargar historial de tipo de cambio
      try {
        const tcRes = await product.getTipoCambioHistory();
        const tcData = tcRes.data?.data || tcRes.data || [];
        setTipoCambioHistory(Array.isArray(tcData) ? tcData : []);
      } catch { setTipoCambioHistory([]); }

      const emptyStats = () => ({ ganadoARS: 0, ganadoUSD: 0, cobrado: 0 });
      const statsPorUsuarioId: Record<number, { ganadoARS: number, ganadoUSD: number, cobrado: number }> = {};
      const statsPorVendedor: Record<string, { ganadoARS: number, ganadoUSD: number, cobrado: number }> = {};

      individuales.forEach((p: any) => {
        const vendedor = normalizeVendorName(p.vendedor_nombre || p.vendedor || "");
        const usuarioId = Number(p.usuario_id || 0);
        if ((!vendedor || vendedor === "sistema") && usuarioId <= 0) return;
        const stats = usuarioId > 0
          ? (statsPorUsuarioId[usuarioId] ||= emptyStats())
          : (statsPorVendedor[vendedor] ||= emptyStats());

        if (p.estado === "Revisado" || p.estado === "Finalizado") {
          stats.ganadoARS += Number(p.comision_vendedor || 0);
          stats.ganadoUSD += Number(p.comision_vendedor_usd || 0);
        }

        const pagos = p.pagosPaquetes || [];
        const totalPagadoAVendedor = pagos
          .filter((pago: any) => !pago.deleted_at && pago.observacion?.includes(TAG_PAGO_VENDEDOR))
          .reduce((acc: number, pago: any) => {
            const importe = parseFloat(pago.importe) || 0;
            const tc = parseFloat(pago.tipo_cambio ?? pago.tipoCambio ?? pago.tc ?? 1);
            return acc + (tc > 1 ? (importe * tc) : importe);
          }, 0);

        stats.cobrado += totalPagadoAVendedor;
      });

      // Construir lista desde todos los usuarios del sistema
      const dataTransformada: VendedorSaldo[] = usuarios.map((u) => {
        const nombreVendedor = String(u.nombre || "").trim();
        const stats = statsPorUsuarioId[u.id] || statsPorVendedor[normalizeVendorName(u.nombre)] || emptyStats();
        return {
          id: `v-${u.id}`,
          usuarioId: u.id,
          vendedor: nombreVendedor || u.username,
          ganadoARS: stats.ganadoARS,
          ganadoUSD: stats.ganadoUSD,
          cobrado: stats.cobrado,
          saldo: stats.ganadoARS - stats.cobrado,
        };
      }).sort((a, b) => b.saldo - a.saldo);

      setVendedoresData(dataTransformada);
    } catch (error) {
      getError("Error al cargar saldos");
    } finally {
      setCargando(false);
    }
  }, [getError]);

  // Tipo de cambio actual: el registro más reciente disponible
  const tcActual = React.useMemo(() => {
    if (tipoCambioHistory.length === 0) return 0;
    const sorted = [...tipoCambioHistory].sort((a, b) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix());
    return Number(sorted[0]?.cotizacion ?? 0);
  }, [tipoCambioHistory]);

  const paquetesFiltradosVendedor = React.useMemo(() => {
    if (!vendedorSeleccionado) return [];
    return paquetesRaw.filter(p => {
      const paqueteUsuarioId = Number(p.usuario_id || 0);
      const mismoUsuario = vendedorSeleccionado.usuarioId > 0 && paqueteUsuarioId === vendedorSeleccionado.usuarioId;
      const mismoNombre = normalizeVendorName(p.vendedor_nombre || p.vendedor || "SISTEMA") === normalizeVendorName(vendedorSeleccionado.vendedor);
      return mismoUsuario || mismoNombre;
    });
  }, [paquetesRaw, vendedorSeleccionado]);

  const [añoSeleccionado, setAñoSeleccionado] = useState<number>(new Date().getFullYear());

  const groupedHistory = React.useMemo(() => {
    const historyByYear: Record<number, Record<string, { entries: any[], totalComisiones: number, totalPagos: number }>> = {};
    const monthsNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    const getTC = (fechaVenta: string): number => {
      if (!fechaVenta || tipoCambioHistory.length === 0) return 0;
      const fecha = dayjs(fechaVenta);
      const desde = fecha.subtract(30, 'day');
      const hasta = fecha.add(30, 'day');
      const enVentana = tipoCambioHistory.filter(tc => { const d = dayjs(tc.created_at); return d.isAfter(desde) && d.isBefore(hasta); });
      if (enVentana.length === 0) {
        const sorted = [...tipoCambioHistory].sort((a, b) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix());
        return Number(sorted[0]?.cotizacion ?? 0);
      }
      return enVentana.reduce((s, tc) => s + Number(tc.cotizacion), 0) / enVentana.length;
    };

    paquetesFiltradosVendedor.forEach(p => {
      if (p.estado === "Revisado" || p.estado === "Finalizado") {
        // BUG FIX: calcular fechaRevisado primero y usarla tanto para agrupar como para mostrar.
        // Antes: fechaGrupo usaba created_at como fallback pero fechaRevisado usaba observaciones,
        // causando que un paquete revisado en Marzo apareciera agrupado en Febrero.
        let fechaRevisado = p.fecha_revision || p.updated_at || p.created_at;
        if (!p.fecha_revision) {
          try {
            const obsStr = p.observaciones;
            let obsArray = [];
            if (typeof obsStr === 'string' && obsStr.startsWith('[')) obsArray = JSON.parse(obsStr);
            else if (Array.isArray(obsStr)) obsArray = obsStr;
            const revObs = obsArray.find((o: any) => (o.descripcion || "").toLowerCase().includes("revisado") && (o.descripcion || "").toLowerCase().includes("estado"));
            if (revObs && revObs.fecha) fechaRevisado = revObs.fecha;
          } catch (e) { }
        }

        // Usar la misma fechaRevisado para agrupar (consistente con lo que se muestra)
        const fechaGrupo = fechaRevisado ? new Date(fechaRevisado) : new Date();
        const year = fechaGrupo.getFullYear();
        const monthName = monthsNames[fechaGrupo.getMonth()];
        if (!historyByYear[year]) historyByYear[year] = {};
        if (!historyByYear[year][monthName]) historyByYear[year][monthName] = { entries: [], totalComisiones: 0, totalPagos: 0 };

        const comisionARS = Number(p.comision_vendedor || 0);
        const comisionUSD = Number(p.comision_vendedor_usd || 0);

        historyByYear[year][monthName].entries.push({
          tipo: 'COMISION',
          fechaRevisado,
          fechaVenta: p.created_at,
          paqueteId: p.id,
          concepto: p.concepto,
          montoARS: comisionARS,
          montoUSD: comisionUSD,
        });
        historyByYear[year][monthName].totalComisiones += comisionARS + comisionUSD * getTC(p.created_at);
      }

      const pagos = p.pagosPaquetes || [];
      pagos.forEach((pag: any) => {
        if (pag.deleted_at || !pag.observacion?.includes(TAG_PAGO_VENDEDOR)) return;

        const match = (pag.observacion || "").match(/\[MES:(.*?)\/(.*?)\]/);
        let monthName, year;

        if (match) {
          monthName = match[1];
          year = parseInt(match[2]);
        } else {
          const date = pag.created_at ? new Date(pag.created_at) : new Date();
          year = date.getFullYear();
          monthName = monthsNames[date.getMonth()];
        }

        if (!historyByYear[year]) historyByYear[year] = {};
        if (!historyByYear[year][monthName]) historyByYear[year][monthName] = { entries: [], totalComisiones: 0, totalPagos: 0 };

        const tc = parseFloat(pag.tipo_cambio ?? pag.tipoCambio ?? pag.tc ?? 1);
        const subtotal = tc > 1 ? (parseFloat(pag.importe) * tc) : parseFloat(pag.importe);
        historyByYear[year][monthName].totalPagos += subtotal;
      });
    });

    return historyByYear;
  }, [paquetesFiltradosVendedor, tipoCambioHistory]);

  const handleAgregarPago = async (mes: string, año: number) => {
    const importeAPagar = importesAPagar[mes];
    if (!importeAPagar || parseFloat(importeAPagar) <= 0) {
      getError("Ingrese un importe válido");
      return;
    }
    const primerPaquete = paquetesFiltradosVendedor[0];
    if (!primerPaquete) {
      getError("El vendedor no tiene paquetes para asociar el pago");
      return;
    }

    try {
      setEnviandoPago(true);
      const data = {
        importe: parseFloat(importeAPagar),
        observacion: `${TAG_PAGO_VENDEDOR} Pago Periodo [MES:${mes}/${año}]`,
        moneda: "ARS",
        tipoCambio: 1,
        id_usuario: 1,
      };
      await product.createPago(primerPaquete.id, data);
      getSuccess(`Pago registrado para ${mes} ${año}`);
      setImportesAPagar(prev => ({ ...prev, [mes]: "" }));
      cargarDatos();
    } catch (error) {
      getError("Error al registrar pago");
    } finally {
      setEnviandoPago(false);
    }
  };

  const handleRecalcularComision = async (paqueteId: number) => {
    setRecalculando(prev => new Set(prev).add(paqueteId));
    try {
      await product.recalcularComision(paqueteId);
      getSuccess("Comisión recalculada");
      cargarDatos();
    } catch {
      getError("Error al recalcular");
    } finally {
      setRecalculando(prev => { const next = new Set(prev); next.delete(paqueteId); return next; });
    }
  };

  const handleRecalcularTodo = async () => {
    setRecalculandoTodo(true);
    try {
      const res = await product.recalcularTodasComisiones();
      const count = res.data?.recalculados ?? "?";
      getSuccess(`${count} comisiones recalculadas`);
      cargarDatos();
    } catch {
      getError("Error al recalcular comisiones");
    } finally {
      setRecalculandoTodo(false);
    }
  };

  const getCambioPromedio = (fechaVenta: string): number | null => {
    if (!fechaVenta || tipoCambioHistory.length === 0) return null;
    const fecha = dayjs(fechaVenta);
    const desde = fecha.subtract(30, 'day');
    const hasta = fecha.add(30, 'day');
    const ratesEnVentana = tipoCambioHistory.filter(tc => {
      const d = dayjs(tc.created_at);
      return d.isAfter(desde) && d.isBefore(hasta);
    });
    if (ratesEnVentana.length === 0) {
      // fallback: rate más reciente
      const sorted = [...tipoCambioHistory].sort((a, b) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix());
      return sorted[0]?.cotizacion ?? null;
    }
    const avg = ratesEnVentana.reduce((s, tc) => s + Number(tc.cotizacion), 0) / ratesEnVentana.length;
    return Math.round(avg * 100) / 100;
  };

  const renderDetalleVendedor = () => {
    if (!vendedorSeleccionado) return null;
    const dataAño = groupedHistory[añoSeleccionado] || {};
    const mesesConData = Object.keys(dataAño)
      .filter(mes => dataAño[mes].entries.length > 0 || dataAño[mes].totalPagos > 0)
      .sort((a, b) => {
        const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        return months.indexOf(b) - months.indexOf(a);
      });

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => setVendedorSeleccionado(null)} sx={{ mr: 1 }}><ArrowBack /></IconButton>
          <Typography variant="h5">Detalle de Cuenta</Typography>
        </Box>

        <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#9c27b0" }}>Vendedor: {vendedorSeleccionado.vendedor}</Typography>
        </Box>

        <Tabs value={añoSeleccionado} onChange={(_, val) => setAñoSeleccionado(val)} sx={{ mb: 2, borderBottom: "1px solid #eee" }}>
          {Object.keys(groupedHistory).map(Number).sort((a, b) => b - a).map(y => <Tab key={y} label={y.toString()} value={y} />)}
        </Tabs>

        {mesesConData.length === 0 ? <Typography sx={{ p: 4, textAlign: 'center', color: '#999' }}>No hay movimientos.</Typography> :
          mesesConData.map(mes => {
            const comisiones = dataAño[mes].entries.filter(e => e.tipo === 'COMISION');
            const saldoPendienteARS = dataAño[mes].totalComisiones - dataAño[mes].totalPagos;

            const estaPagado = saldoPendienteARS <= 0;

            return (
              <Accordion key={mes} sx={{ mb: 2, border: "1px solid #ddd", boxShadow: "none" }} defaultExpanded={!estaPagado}>
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#fff" }} />} sx={{ backgroundColor: estaPagado ? "#4caf50" : "#2196f3", color: "#fff" }}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {mes}{!estaPagado ? ` | $ ${saldoPendienteARS.toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : " | PAGADO"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha revisado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha venta</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nº Presupuesto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cambio promedio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      Monto
                      {isAdmin && (
                      <Tooltip title="Recalcular todas las comisiones">
                        <span>
                          <IconButton size="small" sx={{ ml: 0.5, color: '#555' }}
                            disabled={recalculandoTodo}
                            onClick={handleRecalcularTodo}>
                            {recalculandoTodo ? <CircularProgress size={14} /> : <Refresh fontSize="inherit" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                    </TableCell>
                  </TableRow></TableHead>
                    <TableBody>
                      {comisiones.sort((a, b) => dayjs(b.fechaRevisado).unix() - dayjs(a.fechaRevisado).unix()).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{dayjs(e.fechaRevisado).format("DD/MM/YYYY")}</TableCell>
                          <TableCell>{dayjs(e.fechaVenta).format("DD/MM/YYYY")}</TableCell>
                          <TableCell># {e.paqueteId}</TableCell>
                          <TableCell>{e.concepto || "-"}</TableCell>
                          <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                            {(() => { const cp = getCambioPromedio(e.fechaVenta); return cp ? `$ ${cp.toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—'; })()}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {e.montoARS > 0 && <span style={{ color: '#2e7d32' }}>$ {e.montoARS.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>}
                            {e.montoARS > 0 && e.montoUSD > 0 && <span style={{ color: '#aaa' }}> | </span>}
                            {e.montoUSD > 0 && <span style={{ color: '#1565c0' }}>U$D {e.montoUSD.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>}
                            {isAdmin && (
                            <Tooltip title="Recalcular comisión">
                              <span>
                                <IconButton size="small" sx={{ ml: 0.5, color: '#999' }}
                                  disabled={recalculando.has(e.paqueteId)}
                                  onClick={() => handleRecalcularComision(e.paqueteId)}>
                                  {recalculando.has(e.paqueteId) ? <CircularProgress size={12} /> : <Refresh fontSize="inherit" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table>

                  {isAdmin && !estaPagado && (
                  <Box sx={{ p: 2, bgcolor: '#fcfcfc', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField size="small" placeholder="Importe a pagar" type="number"
                        value={importesAPagar[mes] || ""} onChange={(e) => setImportesAPagar(prev => ({ ...prev, [mes]: e.target.value }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        onKeyPress={(e) => e.key === 'Enter' && handleAgregarPago(mes, añoSeleccionado)} />
                      <Button variant="contained" size="small" disabled={enviandoPago || !importesAPagar[mes]}
                        onClick={() => handleAgregarPago(mes, añoSeleccionado)}
                        sx={{ backgroundColor: "#9c27b0", "&:hover": { backgroundColor: "#7b1fa2" } }}>PAGAR {mes}</Button>
                    </Box>
                  </Box>
                  )}
                  {isAdmin && estaPagado && (
                  <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderTop: '1px solid #eee', textAlign: 'center' }}>
                    <Typography sx={{ color: '#2e7d32', fontWeight: 'bold' }}>✅ Mes cancelado en su totalidad</Typography>
                  </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })
        }
      </Box>
    );
  };

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // RBAC: para no-admins, auto-seleccionar su propio perfil apenas cargan los datos
  useEffect(() => {
    if (!isAdmin && vendedoresData.length > 0 && !vendedorSeleccionado) {
      const miId = Number(userData.id || 0);
      const miNombre = (userData.nombre || "").trim().toLowerCase();
      const miPerfil = vendedoresData.find(v =>
        (miId > 0 && v.usuarioId === miId) ||
        v.vendedor.trim().toLowerCase() === miNombre
      );
      if (miPerfil) setVendedorSeleccionado(miPerfil);
    }
  }, [isAdmin, vendedoresData, vendedorSeleccionado, userData]);

  const datosPaginados = vendedoresData.filter(v => v.vendedor.toLowerCase().includes(terminoBusqueda.toLowerCase())).slice((pagina - 1) * 10, pagina * 10);
  const totalPaginas = Math.ceil(vendedoresData.filter(v => v.vendedor.toLowerCase().includes(terminoBusqueda.toLowerCase())).length / 10);

  return (
    <Box sx={{ mt: 3, pb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4, borderBottom: "1px solid #eee", pb: 2 }}>
        <Typography variant="h5" color="#666">Cuentas Corrientes Vendedores</Typography>
        {isAdmin && <Button variant="contained" sx={{ bgcolor: "#9c27b0", "&:hover": { bgcolor: "#7b1fa2" } }} component={Link} to="/pagos-vendedores">Ir a pagos realizados ➔</Button>}
      </Box>

      {vendedorSeleccionado ? renderDetalleVendedor() : (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <TextField size="small" placeholder="Buscar vendedor..." value={terminoBusqueda} sx={{ width: "350px", bgcolor: "#fff" }}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small"><Search fontSize="small" /></IconButton></InputAdornment> }}
              onChange={(e) => { setTerminoBusqueda(e.target.value); setPagina(1); }} />
          </Box>
          {cargando ? <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><CircularProgress /></Box> : (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee" }}>
              <Table><TableHead><TableRow sx={{ "& th": { fontWeight: "bold", color: "#461264" } }}>
                <TableCell>Acciones</TableCell><TableCell>Monto</TableCell><TableCell>Vendedor</TableCell>
              </TableRow></TableHead>
                <TableBody>
                  {datosPaginados.map((row) => (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                      <TableCell><IconButton size="small" onClick={() => setVendedorSeleccionado(row)} sx={{ color: "#9c27b0" }}><AttachMoney fontSize="small" /></IconButton></TableCell>
                      <TableCell sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                        {(() => {
                          // Convertir todo a pesos usando el TC actual para mostrar en la vista general
                          const totalARS = row.ganadoARS + (tcActual > 0 ? row.ganadoUSD * tcActual : 0) - row.cobrado;
                          if (row.ganadoARS === 0 && row.ganadoUSD === 0) {
                            return <span style={{ color: '#aaa' }}>$ 0</span>;
                          }
                          return (
                            <span style={{ color: totalARS > 0 ? '#2e7d32' : '#e65100' }}>$ {totalARS.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{row.vendedor}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
            </TableContainer>
          )}
          {totalPaginas > 1 && (
            <Box sx={{ display: "flex", mt: 3 }}><Box sx={{ display: "flex", border: "1px solid #dee2e6", borderRadius: "4px", overflow: "hidden" }}>
              <Button disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} sx={{ color: pagina === 1 ? "#6c757d" : "#9c27b0" }}>← Anterior</Button>
              {[...Array(totalPaginas)].map((_, i) => (
                <Button key={i + 1} onClick={() => setPagina(i + 1)} sx={{ bgcolor: pagina === i + 1 ? "#9c27b0" : "#fff", color: pagina === i + 1 ? "#fff" : "#9c27b0" }}>{i + 1}</Button>
              ))}
              <Button disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} sx={{ color: pagina === totalPaginas ? "#6c757d" : "#9c27b0" }}>Siguiente →</Button>
            </Box></Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CuentaCorriente;
